import React, { useState } from 'react';
import { Box, Typography, IconButton, Drawer, List, ListItem, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const NavContainer = styled(Box)({
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`,
  padding: '0 48px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: '64px',
  '@media (max-width: 1024px)': {
    padding: '0 32px'
  },
  '@media (max-width: 768px)': {
    padding: '0 20px'
  }
});

const Logo = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '24px',
  fontWeight: 700,
  color: COLORS.charcoal,
  cursor: 'pointer',
  letterSpacing: '-0.5px',
  textTransform: 'uppercase',
  '@media (max-width: 768px)': {
    fontSize: '18px'
  }
});

const NavLinks = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'center',
  '@media (max-width: 968px)': {
    display: 'none'
  }
});

const NavLink = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.charcoal,
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: COLORS.terracotta
  }
});

const CTAButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 24px',
  backgroundColor: COLORS.charcoal,
  color: COLORS.cream,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: COLORS.terracotta
  },
  '@media (max-width: 968px)': {
    display: 'none'
  }
});

const MobileMenuButton = styled(IconButton)({
  display: 'none',
  color: COLORS.charcoal,
  '@media (max-width: 968px)': {
    display: 'flex'
  }
});

const DrawerHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  backgroundColor: COLORS.cream
});

const DrawerContent = styled(Box)({
  backgroundColor: COLORS.cream,
  height: '100%'
});

const EditorialNavigationHeader = ({ businessInfo }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Services', href: '#services' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Reviews', href: '#reviews' },
    { label: 'Location', href: '#location' },
    { label: 'Hours', href: '#hours' }
  ];

  const handleNavClick = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <NavContainer>
        <Logo onClick={() => handleNavClick('#home')}>
          {businessInfo?.name || 'Business'}
        </Logo>

        <NavLinks>
          {navigationItems.map((item) => (
            <NavLink key={item.label} onClick={() => handleNavClick(item.href)}>
              {item.label}
            </NavLink>
          ))}
        </NavLinks>

        <CTAButton onClick={scrollToContact}>
          Contact Us
        </CTAButton>

        <MobileMenuButton onClick={() => setMobileMenuOpen(true)}>
          <MenuIcon />
        </MobileMenuButton>
      </NavContainer>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: { backgroundColor: COLORS.cream }
        }}
      >
        <DrawerContent sx={{ width: 280 }}>
          <DrawerHeader>
            <Logo sx={{ fontSize: '18px' }}>{businessInfo?.name || 'Business'}</Logo>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: COLORS.charcoal }}>
              <CloseIcon />
            </IconButton>
          </DrawerHeader>
          <List>
            {navigationItems.map((item) => (
              <ListItem
                button
                key={item.label}
                onClick={() => handleNavClick(item.href)}
                sx={{
                  borderBottom: `1px solid ${COLORS.charcoal}20`,
                  '&:hover': {
                    backgroundColor: `${COLORS.terracotta}10`
                  }
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 500,
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: COLORS.charcoal
                  }}
                />
              </ListItem>
            ))}
            <ListItem
              button
              onClick={scrollToContact}
              sx={{
                backgroundColor: COLORS.charcoal,
                marginTop: '16px',
                marginX: '16px',
                '&:hover': {
                  backgroundColor: COLORS.terracotta
                }
              }}
            >
              <ListItemText
                primary="Contact Us"
                primaryTypographyProps={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: COLORS.cream,
                  textAlign: 'center'
                }}
              />
            </ListItem>
          </List>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default EditorialNavigationHeader;
